import cookies from "js-cookie";
import get from "lodash/get";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { ConfigProvider, Flex } from "antd";
import type { ThemeConfig } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import MainLogo from "~/components/Icons/MainLogo";
import PersonIcon from "~/components/Icons/svg/person";
import { loadingMap } from "~/components/Layout/Header/config/config";
import {
  mainNav,
  type MainNavItem,
} from "~/components/Layout/Header/lib/buildMainNav";
import { getShortUserName } from "~/libs/getData";
import { useGetProfileQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";

import { DealerSelector } from "./DealerSelector";
import { NavItem } from "./NavItem";

import s from "./Header.less";

const headerNavDropdownTheme: ThemeConfig = {
  token: {
    colorPrimary: "#f17830",
    controlItemBgHover: "transparent",
    controlItemBgActive: "transparent",
    controlItemBgActiveHover: "transparent",
  },
};

export const Header = () => {
  const { dealerChoice } = useUser();
  const history = useHistory();
  const language = localStorage.getItem("language");
  const { data } = useGetProfileQuery({ fetchPolicy: "no-cache" });
  const fullname: string = get(data, "UserGetter.getProfile.fullname", "");
  const fullnameEn: string = get(data, "UserGetter.getProfile.fullnameEn", "");
  const userName = getShortUserName(language === "en" ? fullnameEn : fullname);

  const { t } = useTranslation();

  const { hasPermission, loading } = usePermissions();

  const handleLogoClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      window.open("/", "_blank");
      return;
    }
    history.push("/");
  };

  const logOut = () => {
    sessionStorage.removeItem("currentDealerId");
    sessionStorage.removeItem("currentDealerName");
    sessionStorage.removeItem("currentDealerNameEn");
    cookies.remove("token");
    window.location.replace("https://tportal.vaz.ru/");
  };

  const nav = useMemo(() => {
    if (loading) return [];

    const filterNavItem = (item: MainNavItem): MainNavItem | null => {
      const hasAccess = item.permissions
        ? item.permissions.length === 0 ||
          item.permissions.some((perm) => hasPermission(perm))
        : false;
      const hasVisibleChildren = item.submenu
        ? item.submenu.some((child) => filterNavItem(child))
        : false;
      if (hasAccess || hasVisibleChildren) {
        return {
          ...item,
          title: t(item.title),
          ...(item.submenu
            ? { submenu: item.submenu.map(filterNavItem).filter(Boolean) }
            : {}),
        };
      }
      return null;
    };

    return mainNav.map(filterNavItem).filter(Boolean) || [];
  }, [mainNav, hasPermission, loading]);

  return (
    <header className={s.headerContainer}>
      <nav className={s.mainNav}>
        <Flex
          className={s.contentWrapper}
          justify="space-between"
          align={"center"}
        >
          <Flex gap={24}>
            <MainLogo style={{ cursor: "pointer" }} onClick={handleLogoClick} />
            {dealerChoice !== "none" && <DealerSelector />}
          </Flex>

          <ConfigProvider theme={headerNavDropdownTheme}>
            <Flex gap={20}>
              {loading
                ? loadingMap.map((el, i) => <Skeleton key={i} width={el} />)
                : nav.map((item, index) => <NavItem item={item} key={index} />)}
            </Flex>
          </ConfigProvider>

          <Flex align="center" gap={20}>
            <Flex align="center" gap={12} className={s.userBlock}>
              <div className={s.userAvatar}>
                <PersonIcon />
              </div>
              <Flex vertical gap={2} style={{ fontSize: 12 }}>
                <span className={s.userName}>{userName}</span>
                <button className={s.logoutBtn} onClick={logOut}>
                  Выйти
                </button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </nav>
    </header>
  );
};
